import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "@/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

// ── Types ──────────────────────────────────────────────────────────────────

type InboundStatus = "open" | "processing" | "done";
type FetchStatus = "idle" | "fetching" | "success" | "error";

type ActiveFolder =
  | { type: "inbox"; statusFilter: "all" | InboundStatus }
  | { type: "archive"; label: string | null }
  | { type: "spam" }
  | { type: "outbound"; sendStatus: "all" | "draft" | "sent" };

interface MailMessage {
  id: string;
  direction?: string;
  folder?: string;
  label?: string | null;
  from?: string;
  fromName?: string;
  to?: string;
  subject?: string;
  body?: string;
  customerEmail?: string;
  customerId?: string | null;
  customerName?: string | null;
  createdAt?: Timestamp;
  status?: InboundStatus;
  sendStatus?: string;
  ownerId?: string | null;
}

const STATUS_LABELS: Record<InboundStatus, string> = {
  open: "未処理",
  processing: "対応中",
  done: "処理済み",
};

const STATUS_STYLES: Record<InboundStatus, string> = {
  open: "bg-orange-100 text-orange-600",
  processing: "bg-blue-100 text-blue-700",
  done: "bg-green-100 text-green-700",
};

function formatDate(timestamp?: Timestamp) {
  if (!timestamp) return "-";
  const d = timestamp.toDate();
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PAGE_SIZE = 50;

// ── Browse (cursor-based, no limit) ────────────────────────────────────────

async function browsePage(
  folder: ActiveFolder,
  cursor: QueryDocumentSnapshot | null
): Promise<{ messages: MailMessage[]; lastDoc: QueryDocumentSnapshot | null; hasMore: boolean }> {
  const base: Parameters<typeof query>[1][] = [];

  if (folder.type === "inbox") {
    base.push(where("direction", "==", "in"), orderBy("createdAt", "desc"));
  } else if (folder.type === "archive") {
    base.push(where("direction", "==", "in"), where("folder", "==", "archive"), orderBy("createdAt", "desc"));
    if (folder.label) base.push(where("label", "==", folder.label));
  } else if (folder.type === "spam") {
    base.push(where("direction", "==", "in"), where("folder", "==", "spam"), orderBy("createdAt", "desc"));
  } else {
    // outbound
    base.push(where("direction", "==", "out"), orderBy("createdAt", "desc"));
  }

  if (cursor) base.push(startAfter(cursor));
  base.push(limit(PAGE_SIZE + 1));

  const snap = await getDocs(query(collection(db, "mail_messages"), ...base));
  const hasMore = snap.docs.length > PAGE_SIZE;
  const docs = snap.docs.slice(0, PAGE_SIZE);

  let messages = docs.map((d) => ({ id: d.id, ...(d.data() as MailMessage) }));
  // inbox: legacy compat — treat missing folder as inbox
  if (folder.type === "inbox") {
    messages = messages.filter((m) => !m.folder || m.folder === "inbox");
  }
  // outbound status filter
  if (folder.type === "outbound" && folder.sendStatus !== "all") {
    messages = messages.filter((m) =>
      folder.sendStatus === "draft" ? m.sendStatus === "draft" : m.sendStatus !== "draft"
    );
  }
  // inbox status filter
  if (folder.type === "inbox" && folder.statusFilter !== "all") {
    messages = messages.filter((m) => m.status === folder.statusFilter);
  }

  return { messages, lastDoc: docs[docs.length - 1] ?? null, hasMore };
}

// ── Search (Firestore forward-match) ───────────────────────────────────────

async function searchMessages(
  folder: ActiveFolder,
  field: "subject" | "from" | "to",
  term: string
): Promise<MailMessage[]> {
  const upper = term + "";
  const base: Parameters<typeof query>[1][] = [];

  if (folder.type === "inbox" || folder.type === "archive" || folder.type === "spam") {
    base.push(where("direction", "==", "in"));
    if (folder.type === "archive") base.push(where("folder", "==", "archive"));
    if (folder.type === "spam") base.push(where("folder", "==", "spam"));
  } else {
    base.push(where("direction", "==", "out"));
  }

  base.push(where(field, ">=", term), where(field, "<=", upper), orderBy(field), limit(500));

  const snap = await getDocs(query(collection(db, "mail_messages"), ...base));
  let messages = snap.docs.map((d) => ({ id: d.id, ...(d.data() as MailMessage) }));

  if (folder.type === "inbox") {
    messages = messages.filter((m) => !m.folder || m.folder === "inbox");
    if (folder.statusFilter !== "all") messages = messages.filter((m) => m.status === folder.statusFilter);
  }
  if (folder.type === "outbound" && folder.sendStatus !== "all") {
    messages = messages.filter((m) =>
      folder.sendStatus === "draft" ? m.sendStatus === "draft" : m.sendStatus !== "draft"
    );
  }
  if (folder.type === "archive" && folder.label) {
    messages = messages.filter((m) => m.label === folder.label);
  }

  return messages;
}

// ── Inbox counts (for sidebar) ─────────────────────────────────────────────

async function fetchInboxAll(): Promise<MailMessage[]> {
  const snap = await getDocs(
    query(collection(db, "mail_messages"), where("direction", "==", "in"), orderBy("createdAt", "desc"), limit(2000))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as MailMessage) }))
    .filter((m) => !m.folder || m.folder === "inbox");
}

async function fetchArchiveLabels(): Promise<string[]> {
  const snap = await getDocs(
    query(collection(db, "mail_messages"), where("direction", "==", "in"), where("folder", "==", "archive"), orderBy("createdAt", "desc"), limit(500))
  );
  const labels = snap.docs.map((d) => d.data().label as string | undefined).filter((l): l is string => !!l);
  return [...new Set(labels)];
}

// ── Component ───────────────────────────────────────────────────────────────

export default function MailListPage() {
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState<ActiveFolder>({ type: "inbox", statusFilter: "open" });
  const [archiveLabels, setArchiveLabels] = useState<string[]>([]);
  const [inboxMessages, setInboxMessages] = useState<MailMessage[]>([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>("idle");
  const [fetchMessage, setFetchMessage] = useState<string | null>(null);

  // Search state
  const [subjectInput, setSubjectInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [activeSearch, setActiveSearch] = useState<{ field: "subject" | "from" | "to"; term: string } | null>(null);
  const [searchPage, setSearchPage] = useState(1);

  // Browse cursor state
  const [cursorStack, setCursorStack] = useState<(QueryDocumentSnapshot | null)[]>([null]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Selection / bulk processing state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const markMessagesDone = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkProcessing(true);
    try {
      const currentUser = auth.currentUser;
      await Promise.all(
        ids.map((id) =>
          updateDoc(doc(db, "mail_messages", id), {
            status: "done",
            lastUpdatedBy: currentUser?.uid ?? null,
            lastUpdatedByName: currentUser?.displayName || currentUser?.email || null,
            lastUpdatedAt: serverTimestamp(),
          })
        )
      );
      const idSet = new Set(ids);
      setInboxMessages((prev) => prev.map((m) => (idSet.has(m.id) ? { ...m, status: "done" as InboundStatus } : m)));
      setMessages((prev) => prev.map((m) => (idSet.has(m.id) ? { ...m, status: "done" as InboundStatus } : m)));
      clearSelection();
    } catch (err) {
      console.error("一括処理エラー:", err);
      alert("処理済みへの更新に失敗しました。");
    } finally {
      if (mountedRef.current) setBulkProcessing(false);
    }
  };

  const inboxCounts = useMemo(
    () => ({
      all: inboxMessages.length,
      open: inboxMessages.filter((m) => m.status === "open").length,
      processing: inboxMessages.filter((m) => m.status === "processing").length,
      done: inboxMessages.filter((m) => m.status === "done").length,
    }),
    [inboxMessages]
  );

  // 受信トレイのステータスタブ切替はFirestore再取得せず、読み込み済みinboxMessagesをその場でフィルタする
  const isInboxBrowse = activeFolder.type === "inbox" && !activeSearch;
  const filteredInboxMessages = useMemo(() => {
    if (activeFolder.type !== "inbox") return [];
    return activeFolder.statusFilter === "all"
      ? inboxMessages
      : inboxMessages.filter((m) => m.status === activeFolder.statusFilter);
  }, [inboxMessages, activeFolder]);

  // Browse: load page by cursor index
  const loadBrowsePage = async (folder: ActiveFolder, stack: (QueryDocumentSnapshot | null)[], index: number) => {
    setLoading(true);
    setError(null);
    try {
      const cursor = stack[index] ?? null;
      const result = await browsePage(folder, cursor);
      if (mountedRef.current) {
        setMessages(result.messages);
        setHasMore(result.hasMore);
        // Store cursor for next page if not already stored
        if (result.hasMore && result.lastDoc) {
          const newStack = [...stack.slice(0, index + 1), result.lastDoc];
          setCursorStack(newStack);
        }
      }
    } catch (err) {
      console.error("mail_messages 取得エラー:", err);
      if (mountedRef.current) setError("メール一覧の取得に失敗しました。");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // Search: run Firestore forward-match query
  const runSearch = async (folder: ActiveFolder, field: "subject" | "from" | "to", term: string) => {
    setLoading(true);
    setError(null);
    try {
      const results = await searchMessages(folder, field, term);
      if (mountedRef.current) {
        setMessages(results);
        setHasMore(false);
      }
    } catch (err) {
      console.error("検索エラー:", err);
      if (mountedRef.current) setError("検索に失敗しました。");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const loadInboxCounts = async () => {
    setInboxLoading(true);
    try {
      const data = await fetchInboxAll();
      if (mountedRef.current) setInboxMessages(data);
    } catch { /* ignore */ }
    finally {
      if (mountedRef.current) setInboxLoading(false);
    }
  };

  // 受信トレイのブラウズはinboxMessagesを使うためFirestore再取得は不要。
  // それ以外のフォルダのみFirestoreへ問い合わせる。
  const refreshBrowseView = (folder: ActiveFolder) => {
    const stack = [null] as (QueryDocumentSnapshot | null)[];
    setCursorStack(stack);
    setCursorIndex(0);
    if (folder.type !== "inbox") {
      loadBrowsePage(folder, stack, 0);
    }
  };

  const resetBrowse = (folder: ActiveFolder) => {
    setActiveSearch(null);
    setSubjectInput("");
    setEmailInput("");
    setSearchPage(1);
    clearSelection();
    refreshBrowseView(folder);
  };

  const switchFolder = (folder: ActiveFolder) => {
    setActiveFolder(folder);
    resetBrowse(folder);
    if (folder.type !== "inbox") loadInboxCounts();
    if (folder.type === "archive") {
      fetchArchiveLabels().then((labels) => {
        if (mountedRef.current) setArchiveLabels(labels);
      });
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadInboxCounts();
    if (activeFolder.type !== "inbox") {
      loadBrowsePage(activeFolder, [null], 0);
    }
    fetchArchiveLabels().then((labels) => {
      if (mountedRef.current) setArchiveLabels(labels);
    });
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const sTerm = subjectInput.trim();
    const eTerm = emailInput.trim();
    clearSelection();
    if (!sTerm && !eTerm) {
      // Clear search → back to browse
      setActiveSearch(null);
      setSearchPage(1);
      refreshBrowseView(activeFolder);
      return;
    }
    // Prioritize subject; if only email filled, use that
    const field: "subject" | "from" | "to" = sTerm
      ? "subject"
      : activeFolder.type === "outbound" ? "to" : "from";
    const term = sTerm || eTerm;
    const search = { field, term };
    setActiveSearch(search);
    setSearchPage(1);
    runSearch(activeFolder, field, term);
  };

  const clearSearch = () => {
    setSubjectInput("");
    setEmailInput("");
    setActiveSearch(null);
    setSearchPage(1);
    clearSelection();
    refreshBrowseView(activeFolder);
  };

  const handleFetchNow = async () => {
    if (fetchStatus === "fetching") return;
    setFetchStatus("fetching");
    setFetchMessage(null);
    try {
      const functions = getFunctions(undefined, "asia-northeast1");
      const fn = httpsCallable<unknown, { success: boolean; saved: number; skipped: number; spam: number }>(functions, "fetchMailsNow");
      const result = await fn({});
      const { saved, skipped, spam } = result.data;
      const parts = [`${saved}件保存`];
      if (skipped > 0) parts.push(`${skipped}件スキップ`);
      if (spam > 0) parts.push(`${spam}件スパム`);
      setFetchMessage(`受信完了：${parts.join("、")}`);
      setFetchStatus("success");
      // Refresh
      if (activeSearch) {
        runSearch(activeFolder, activeSearch.field, activeSearch.term);
      } else {
        refreshBrowseView(activeFolder);
      }
      loadInboxCounts();
    } catch (err: unknown) {
      setFetchMessage(`エラー：${err instanceof Error ? err.message : "不明"}`);
      setFetchStatus("error");
    } finally {
      setTimeout(() => {
        if (mountedRef.current) { setFetchStatus("idle"); setFetchMessage(null); }
      }, 3000);
    }
  };

  // Search pagination
  const searchTotalPages = useMemo(() => Math.max(1, Math.ceil(messages.length / PAGE_SIZE)), [messages]);
  const pagedSearchMessages = useMemo(
    () => messages.slice((searchPage - 1) * PAGE_SIZE, searchPage * PAGE_SIZE),
    [messages, searchPage]
  );

  const pagedInboxMessages = useMemo(
    () => filteredInboxMessages.slice(cursorIndex * PAGE_SIZE, cursorIndex * PAGE_SIZE + PAGE_SIZE),
    [filteredInboxMessages, cursorIndex]
  );

  const displayMessages = activeSearch
    ? pagedSearchMessages
    : isInboxBrowse
      ? pagedInboxMessages
      : messages;

  const effectiveHasMore = isInboxBrowse
    ? (cursorIndex + 1) * PAGE_SIZE < filteredInboxMessages.length
    : hasMore;

  const listLoading = isInboxBrowse ? inboxLoading : loading;

  // Browse navigation
  const browseNext = () => {
    const newIndex = cursorIndex + 1;
    setCursorIndex(newIndex);
    clearSelection();
    if (!isInboxBrowse) loadBrowsePage(activeFolder, cursorStack, newIndex);
  };
  const browsePrev = () => {
    const newIndex = cursorIndex - 1;
    setCursorIndex(newIndex);
    clearSelection();
    if (!isInboxBrowse) loadBrowsePage(activeFolder, cursorStack, newIndex);
  };

  // ── Sidebar helpers ────────────────────────────────────────────────────

  const isFolderActive = (folder: ActiveFolder) => {
    if (folder.type !== activeFolder.type) return false;
    if (folder.type === "inbox" && activeFolder.type === "inbox")
      return folder.statusFilter === activeFolder.statusFilter;
    if (folder.type === "archive" && activeFolder.type === "archive")
      return folder.label === activeFolder.label;
    if (folder.type === "outbound" && activeFolder.type === "outbound")
      return folder.sendStatus === activeFolder.sendStatus;
    return true;
  };

  const SideBtn = ({ folder, icon, label, count, indent }: {
    folder: ActiveFolder; icon?: string; label: string; count?: number; indent?: boolean;
  }) => {
    const active = isFolderActive(folder);
    return (
      <button
        type="button"
        onClick={() => switchFolder(folder)}
        className={`flex w-full items-center justify-between rounded-lg px-3 text-left transition ${
          indent ? "ml-3 py-1.5 text-xs" : "py-2 text-sm"
        } ${active ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}
      >
        <span className="flex items-center gap-1.5">
          {icon && <span>{icon}</span>}
          {label}
        </span>
        {count !== undefined && (
          <span className={`text-xs rounded-full px-1.5 py-0.5 ${active ? "bg-blue-100 text-blue-700 font-bold" : "text-gray-400"}`}>
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* ヘッダー */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm text-gray-500">ホーム &gt; メール管理</p>
            <h1 className="mt-1 text-2xl font-bold text-gray-800">メール管理</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {fetchMessage && (
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${fetchStatus === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                {fetchMessage}
              </span>
            )}
            <button onClick={handleFetchNow} disabled={fetchStatus === "fetching"}
              className={`inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-semibold shadow-sm transition ${
                fetchStatus === "fetching" ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed" : "border-green-600 bg-white text-green-700 hover:bg-green-50"
              }`}>
              {fetchStatus === "fetching" ? "受信中..." : "📥 今すぐ受信"}
            </button>
            <button onClick={() => navigate("/admin/mail/rules")}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50">
              ⚙️ 振り分けルール
            </button>
            <button onClick={() => navigate("/admin/mail/templates")}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50">
              📄 テンプレート
            </button>
            <button onClick={() => navigate("/admin/mail/new")}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700">
              ＋ 新規作成
            </button>
          </div>
        </div>

        <div className="flex gap-4">
          {/* ─── サイドバー ─── */}
          <aside className="w-52 shrink-0 rounded-xl bg-white p-3 shadow-sm self-start space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 px-2 mb-1">受信</p>
              <div className="space-y-0.5">
                <SideBtn folder={{ type: "inbox", statusFilter: "all" }} icon="📥" label="受信メール" count={inboxCounts.all} />
                {activeFolder.type === "inbox" && (
                  <div className="space-y-0.5">
                    <SideBtn folder={{ type: "inbox", statusFilter: "open" }} label="未処理" count={inboxCounts.open} indent />
                    <SideBtn folder={{ type: "inbox", statusFilter: "processing" }} label="対応中" count={inboxCounts.processing} indent />
                    <SideBtn folder={{ type: "inbox", statusFilter: "done" }} label="処理済み" count={inboxCounts.done} indent />
                  </div>
                )}
                <SideBtn folder={{ type: "archive", label: null }} icon="📁" label="アーカイブ" />
                {activeFolder.type === "archive" && archiveLabels.length > 0 && (
                  <div className="space-y-0.5">
                    {archiveLabels.map((lbl) => (
                      <SideBtn key={lbl} folder={{ type: "archive", label: lbl }} label={lbl} indent />
                    ))}
                  </div>
                )}
                <SideBtn folder={{ type: "spam" }} icon="🚫" label="迷惑メール" />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 px-2 mb-1">送信</p>
              <div className="space-y-0.5">
                <SideBtn folder={{ type: "outbound", sendStatus: "draft" }} icon="📝" label="下書き" />
                <SideBtn folder={{ type: "outbound", sendStatus: "sent" }} icon="📤" label="送信済み" />
              </div>
            </div>
          </aside>

          {/* ─── メインコンテンツ ─── */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* 検索バー */}
            <div className="bg-white rounded-xl shadow-sm p-3 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[140px] space-y-1">
                <label className="text-xs text-gray-500 font-medium">件名（前方一致）</label>
                <input
                  type="text"
                  placeholder="件名で検索..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={subjectInput}
                  onChange={(e) => setSubjectInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <div className="flex-1 min-w-[140px] space-y-1">
                <label className="text-xs text-gray-500 font-medium">
                  {activeFolder.type === "outbound" ? "宛先メールアドレス" : "送信元メールアドレス"}（前方一致）
                </label>
                <input
                  type="text"
                  placeholder="メールアドレスで検索..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSearch}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                  検索
                </button>
                {activeSearch && (
                  <button onClick={clearSearch}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                    クリア
                  </button>
                )}
              </div>
              {activeSearch && (
                <div className="w-full text-xs text-blue-600 font-medium">
                  「{activeSearch.term}」で検索中 — {messages.length}件ヒット
                </div>
              )}
            </div>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
                <span className="text-sm font-medium text-blue-700">{selectedIds.size}件選択中</span>
                <button
                  onClick={() => markMessagesDone([...selectedIds])}
                  disabled={bulkProcessing}
                  className="ml-auto px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  {bulkProcessing ? "処理中..." : "✓ 選択した項目を処理済みにする"}
                </button>
                <button
                  onClick={clearSelection}
                  disabled={bulkProcessing}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50"
                >
                  選択解除
                </button>
              </div>
            )}

            <div className="relative bg-white rounded-xl shadow-sm divide-y">
              {listLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-500 animate-spin" />
                    読み込み中...
                  </div>
                </div>
              )}
              {error && <div className="p-4 text-red-600 text-sm">{error}</div>}
              {!listLoading && !error && displayMessages.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-400">メールがありません</div>
              )}
              {displayMessages.map((msg, idx) => {
                const isInbound = msg.direction === "in";
                const displayName = isInbound
                  ? msg.fromName || msg.from || "(送信者不明)"
                  : `To: ${msg.to || "-"}`;
                const avatar = displayName[0]?.toUpperCase() ?? "?";

                const isDone = msg.status === "done";

                return (
                  // 表示位置(idx)をkeyにして、フィルタ切替時も同じDOM要素を使い回す。
                  // msg.idをkeyにすると切替のたびに要素が作り直され、カーソル直下で要素が
                  // 入れ替わることでブラウザのヒットテストが古いまま残りクリックが効かなくなる。
                  <div key={idx} className="w-full flex items-start gap-2 px-4 py-3 hover:bg-blue-50 transition">
                    {isInbound && (
                      <input
                        type="checkbox"
                        className="mt-1.5 h-4 w-4 shrink-0 rounded border-gray-300"
                        checked={selectedIds.has(msg.id)}
                        onChange={() => toggleSelect(msg.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <button className="flex-1 min-w-0 text-left"
                      onClick={() => navigate(`/admin/mail/messages/${msg.id}`)}>
                    <div className="flex items-start gap-3">
                      <div className="mt-1 h-8 w-8 shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                        {avatar}
                      </div>
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isInbound && msg.status && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[msg.status as InboundStatus] ?? STATUS_STYLES["open"]}`}>
                              {STATUS_LABELS[msg.status as InboundStatus] ?? STATUS_LABELS["open"]}
                            </span>
                          )}
                          {!isInbound && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              msg.sendStatus === "sent" ? "bg-green-100 text-green-700"
                              : msg.sendStatus === "draft" ? "bg-yellow-100 text-yellow-700"
                              : msg.sendStatus === "error" ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                            }`}>
                              {msg.sendStatus === "sent" ? "送信済み" : msg.sendStatus === "draft" ? "下書き" : msg.sendStatus === "error" ? "送信エラー" : "送信待ち"}
                            </span>
                          )}
                          {msg.label && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{msg.label}</span>
                          )}
                          <span className="text-sm font-semibold text-gray-800 truncate">{displayName}</span>
                        </div>
                        <div className="text-sm text-gray-700 font-medium truncate">{msg.subject || "(件名なし)"}</div>
                        {msg.customerName && (
                          <div className="text-xs" onClick={(e) => e.stopPropagation()}>
                            {msg.customerId
                              ? <a href={`/admin/users/${msg.customerId}`} className="text-blue-500 hover:underline">👤 {msg.customerName}</a>
                              : <span className="text-gray-400">👤 {msg.customerName}</span>
                            }
                          </div>
                        )}
                        <div className="text-xs text-gray-400 truncate">
                          {(msg.body ?? "").replace(/\n/g, " ").slice(0, 80)}
                        </div>
                      </div>
                      <div className="shrink-0 text-xs text-gray-400 whitespace-nowrap">
                        {formatDate(msg.createdAt)}
                      </div>
                    </div>
                    </button>
                    {isInbound && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markMessagesDone([msg.id]);
                        }}
                        disabled={isDone || bulkProcessing}
                        className="shrink-0 self-center px-3 py-1.5 rounded-lg border text-xs font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed border-green-600 text-green-700 hover:bg-green-50"
                      >
                        {isDone ? "処理済み" : "✓ 処理済みにする"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ページネーション */}
            {!listLoading && (
              <div className="flex items-center justify-between px-1">
                {activeSearch ? (
                  // 検索結果: ページ番号あり
                  <>
                    <span className="text-xs text-gray-500">
                      {messages.length}件中 {(searchPage - 1) * PAGE_SIZE + 1}〜{Math.min(searchPage * PAGE_SIZE, messages.length)}件表示
                    </span>
                    {searchTotalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSearchPage(1)} disabled={searchPage === 1}
                          className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
                        <button onClick={() => setSearchPage((p) => Math.max(1, p - 1))} disabled={searchPage === 1}
                          className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
                        {Array.from({ length: searchTotalPages }, (_, i) => i + 1)
                          .filter((p) => p === 1 || p === searchTotalPages || Math.abs(p - searchPage) <= 2)
                          .reduce<(number | "...")[]>((acc, p, i, arr) => {
                            if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("...");
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, i) =>
                            p === "..." ? (
                              <span key={`e-${i}`} className="px-2 py-1 text-xs text-gray-400">…</span>
                            ) : (
                              <button key={p} onClick={() => setSearchPage(p as number)}
                                className={`px-2.5 py-1 text-xs rounded border ${searchPage === p ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 hover:bg-gray-50"}`}>
                                {p}
                              </button>
                            )
                          )}
                        <button onClick={() => setSearchPage((p) => Math.min(searchTotalPages, p + 1))} disabled={searchPage === searchTotalPages}
                          className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
                        <button onClick={() => setSearchPage(searchTotalPages)} disabled={searchPage === searchTotalPages}
                          className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
                      </div>
                    )}
                  </>
                ) : (
                  // ブラウズ: 前へ/次へ
                  <>
                    <span className="text-xs text-gray-500">
                      {cursorIndex > 0 ? `${cursorIndex * PAGE_SIZE + 1}件目〜` : `最新${displayMessages.length}件`}
                    </span>
                    <div className="flex items-center gap-2">
                      <button onClick={browsePrev} disabled={cursorIndex === 0}
                        className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 font-medium">
                        ‹ 前へ
                      </button>
                      <button onClick={browseNext} disabled={!effectiveHasMore}
                        className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 font-medium">
                        次へ ›
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
